import { useCallback, useEffect, useState } from "react";
import { Copy, Loader2, MessageCircle, RefreshCw, Share2, Users, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const TABS = [
  { id: "share", label: "Partager", icon: Share2 },
  { id: "online", label: "En ligne", icon: Users },
];

function shareMessage(hostName, inviteLink) {
  const who = hostName ? `${hostName} t'invite` : "Je t'invite";
  return `${who} à une partie de Perē Fa'anu'u sur Faanuu !\n${inviteLink}`;
}

export default function InviteModal({ open, onOpenChange, inviteLink, roomId, hostName = "" }) {
  const [tab, setTab] = useState("share");
  const [onlineUsers, setOnlineUsers] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const copyLink = useCallback(async (label = "Lien d'invitation copié !") => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success(label);
    } catch {
      toast.error("Impossible de copier le lien.");
    }
  }, [inviteLink]);

  const fetchOnlineUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      await api.post("/games/online/presence");
      const { data } = await api.get("/games/online/online-users");
      setOnlineUsers(data);
    } catch (e) {
      setOnlineUsers([]);
      toast.error(formatApiError(e));
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    api.post("/games/online/presence").catch(() => {});
    if (tab === "online") fetchOnlineUsers();
  }, [open, tab, fetchOnlineUsers]);

  const shareWhatsApp = () => {
    const text = shareMessage(hostName, inviteLink);
    window.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const shareMessenger = async () => {
    const link = encodeURIComponent(inviteLink);
    const messengerWeb = `https://m.me/share?link=${link}`;
    const opened = window.open(messengerWeb, "_blank", "noopener,noreferrer");
    if (!opened) {
      await copyLink("Lien copié — collez-le dans Messenger.");
    }
  };

  const invitePlayer = async (target) => {
    if (!roomId) {
      await copyLink(`Lien copié — envoyez-le à ${target.name}.`);
      return;
    }
    try {
      await api.post(`/games/online/${roomId}/invite-user`, { user_id: target.id });
      toast.success(`Invitation envoyée à ${target.name} !`);
    } catch {
      await copyLink(`Lien copié — envoyez-le à ${target.name}.`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[color:var(--surface)] border-[color:var(--border)] text-[color:var(--text-primary)] sm:max-w-md"
        data-testid="invite-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">Inviter un adversaire</DialogTitle>
          <DialogDescription className="text-[color:var(--text-secondary)] text-sm">
            Partagez le lien de la partie pour qu&apos;un ami vous rejoigne.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 p-1 rounded-md bg-[color:var(--surface-glass)] border border-[color:var(--border)]">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={[
                "flex-1 py-2 px-2 text-xs rounded flex items-center justify-center gap-1.5 transition",
                tab === id ? "btn-gold" : "text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]",
              ].join(" ")}
              data-testid={`invite-tab-${id}`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        <div className="min-h-[180px]">
          {tab === "share" && (
            <div className="space-y-3 py-2" data-testid="invite-share-panel">
              <p className="text-sm text-[color:var(--text-secondary)] text-center">
                Envoyez le lien par message à vos amis.
              </p>
              <button
                type="button"
                onClick={shareWhatsApp}
                className="w-full py-3 rounded-md text-sm font-medium flex items-center justify-center gap-2 bg-[#25D366] text-white hover:bg-[#20BD5A] transition"
                data-testid="invite-whatsapp"
              >
                <MessageCircle size={18} /> WhatsApp
              </button>
              <button
                type="button"
                onClick={shareMessenger}
                className="w-full py-3 rounded-md text-sm font-medium flex items-center justify-center gap-2 bg-[#0084FF] text-white hover:bg-[#0074E0] transition"
                data-testid="invite-messenger"
              >
                <MessageCircle size={18} /> Messenger
              </button>
            </div>
          )}

          {tab === "online" && (
            <div className="space-y-3" data-testid="invite-online-panel">
              <div className="flex items-center justify-between">
                <p className="text-xs text-[color:var(--text-secondary)]">Joueurs connectés récemment</p>
                <button
                  type="button"
                  onClick={fetchOnlineUsers}
                  disabled={loadingUsers}
                  className="text-xs flex items-center gap-1 text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"
                  data-testid="invite-online-refresh"
                >
                  <RefreshCw size={12} className={loadingUsers ? "animate-spin" : ""} /> Actualiser
                </button>
              </div>
              <div className="glass rounded-lg max-h-48 overflow-y-auto divide-y divide-[color:var(--border)]">
                {loadingUsers && onlineUsers === null && (
                  <div className="p-6 text-center text-xs text-[color:var(--text-secondary)] flex items-center justify-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Chargement…
                  </div>
                )}
                {!loadingUsers && onlineUsers?.length === 0 && (
                  <div className="p-6 text-center text-xs text-[color:var(--text-secondary)]">
                    Aucun joueur en ligne pour le moment.
                  </div>
                )}
                {onlineUsers?.map((u) => (
                  <div key={u.id} className="flex items-center justify-between gap-2 px-3 py-2.5 text-sm">
                    <span className="font-medium truncate">{u.name}</span>
                    <button
                      type="button"
                      onClick={() => invitePlayer(u)}
                      className="shrink-0 px-3 py-1.5 rounded-md btn-gold text-xs flex items-center gap-1"
                      data-testid={`invite-user-${u.id}`}
                    >
                      <UserPlus size={12} /> Inviter
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-[color:var(--text-secondary)]">
                Le lien sera copié — envoyez-le au joueur (chat, message…).
              </p>
            </div>
          )}
        </div>

        <div className="pt-2 border-t border-[color:var(--border)]">
          <button
            type="button"
            onClick={() => copyLink()}
            className="w-full py-2.5 rounded-md border border-[color:var(--border)] text-xs flex items-center justify-center gap-2 hover:bg-[color:var(--surface-glass)]"
            data-testid="invite-copy-link"
          >
            <Copy size={14} /> Copier le lien d&apos;invitation
          </button>
          <p className="mt-2 text-[10px] text-center text-[color:var(--text-secondary)] break-all font-mono">
            {inviteLink}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
